/* BillingExportDialog — iter102.36
 *
 * Date-range + format + payment-method chooser for the Doctor Billing
 * "Export" button.
 *
 * Three dials:
 *   · Date range presets + custom from/to
 *   · Format toggle: CSV / PDF
 *   · Payment methods multi-select (Cash · UPI · Card · Bank · Cheque · Other)
 *
 * The Payment Methods chip row is the ONLY control that decides whether
 * cash rides along in the export — the on-screen "Hide cash" discretion
 * toggle deliberately does NOT influence exports (iter102.36 decouple:
 * Hide-cash is a screen-only concern; Export is a deliberate accounting
 * action, so mixing the two just added cognitive load).
 *
 * The dialog is prefilled from the on-screen state (search + status
 * chip) but shows those as a read-only "also applying" line so the
 * doctor knows exactly what's about to leave the browser. The list
 * itself is passed in as a prop (already filtered upstream by status
 * + search) so the CSV branch is 100% client-side; the PDF branch
 * POSTs to the backend with the same date + method filters.
 */
import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    X, DownloadSimple, FileText, Table, CalendarBlank, Sparkle,
    Wallet, Check,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/* ─────────────── Date presets ─────────────── */
function ymd(d) {
    return d.toISOString().slice(0, 10);
}
function computePreset(key) {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    switch (key) {
        case "this_month":
            return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
        case "last_month":
            return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
        case "this_quarter": {
            const qStart = Math.floor(m / 3) * 3;
            return { from: ymd(new Date(y, qStart, 1)), to: ymd(new Date(y, qStart + 3, 0)) };
        }
        case "this_fy": {
            // Indian FY: 1 April → 31 March
            const fyStart = m >= 3 ? y : y - 1;
            return { from: `${fyStart}-04-01`, to: `${fyStart + 1}-03-31` };
        }
        case "all_time":
            return { from: null, to: null };
        default:
            return { from: null, to: null };
    }
}

const PRESETS = [
    { key: "this_month",   label: "This month" },
    { key: "last_month",   label: "Last month" },
    { key: "this_quarter", label: "This quarter" },
    { key: "this_fy",      label: "This FY (Apr–Mar)" },
    { key: "all_time",     label: "All time" },
    { key: "custom",       label: "Custom" },
];

const METHOD_LABEL = {
    cash: "Cash", upi: "UPI", card: "Card",
    bank_transfer: "Bank", cheque: "Cheque", other: "Other",
};
const METHOD_CHIPS = [
    { key: "cash",          label: "Cash",     tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { key: "upi",           label: "UPI",      tone: "bg-primary/10 text-primary border-primary/25" },
    { key: "card",          label: "Card",     tone: "bg-violet-50 text-violet-700 border-violet-200" },
    { key: "bank_transfer", label: "Bank",     tone: "bg-slate-50 text-slate-700 border-slate-200" },
    { key: "cheque",        label: "Cheque",   tone: "bg-slate-50 text-slate-700 border-slate-200" },
    { key: "other",         label: "Other",    tone: "bg-slate-50 text-slate-700 border-slate-200" },
];
const ALL_METHOD_KEYS = METHOD_CHIPS.map((m) => m.key);

/* ─────────────── CSV helpers ─────────────── */
function csvEscape(v) {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}
function downloadCsv(rows, filename) {
    if (!rows.length) {
        toast.error("No invoices in that range.");
        return;
    }
    const cols = Object.keys(rows[0]);
    const body = rows
        .map((r) => cols.map((c) => csvEscape(r[c])).join(","))
        .join("\n");
    const csv = cols.join(",") + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}


export default function BillingExportDialog({
    open,
    onClose,
    invoices,          // status/search-filtered raw list (no Hide-cash)
    statusChipLabel,   // e.g. "Overdue" (or null)
    searchQuery,       // current search string (or null)
}) {
    const [preset, setPreset] = useState("this_month");
    const [customFrom, setCustomFrom] = useState(computePreset("this_month").from);
    const [customTo, setCustomTo] = useState(computePreset("this_month").to);
    const [format, setFormat] = useState("csv");
    const [selectedMethods, setSelectedMethods] = useState(() => new Set(ALL_METHOD_KEYS));
    const [busy, setBusy] = useState(false);

    const allMethodsSelected = selectedMethods.size === ALL_METHOD_KEYS.length;
    const noMethodsSelected = selectedMethods.size === 0;

    const toggleMethod = (key) => {
        setSelectedMethods((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Resolve the actual from/to being used
    const resolvedRange = useMemo(() => {
        if (preset === "custom") {
            return { from: customFrom || null, to: customTo || null };
        }
        return computePreset(preset);
    }, [preset, customFrom, customTo]);

    // Client-side date + method filter for the CSV branch — the parent already
    // applied status + search, so we only need to add the date + method filters
    // that the dialog collected. Empty from/to means "no bound".
    const rangedCsvRows = useMemo(() => {
        const { from, to } = resolvedRange;
        const fromStamp = from ? `${from}T00:00:00` : null;
        const toStamp = to ? `${to}T23:59:59` : null;
        const filterByMethod = !allMethodsSelected;
        return invoices.filter((inv) => {
            const stamp = inv.issued_at || inv.created_at || "";
            if (fromStamp && stamp < fromStamp) return false;
            if (toStamp && stamp > toStamp) return false;
            if (filterByMethod) {
                const payments = inv.payments || [];
                if (!payments.length) return false;
                const hit = payments.some((p) => {
                    const m = (p.method || "cash").toLowerCase();
                    return selectedMethods.has(m);
                });
                if (!hit) return false;
            }
            return true;
        });
    }, [invoices, resolvedRange, allMethodsSelected, selectedMethods]);

    if (!open) return null;

    const handleCsv = () => {
        const filterByMethod = !allMethodsSelected;
        const rows = rangedCsvRows.map((inv) => {
            // Only count payments that match the current method filter.
            // The "Paid" column then reflects exactly what's in the
            // selected buckets.
            let paid = 0;
            const methods = [];
            for (const p of (inv.payments || [])) {
                const m = (p.method || "cash").toLowerCase();
                if (filterByMethod && !selectedMethods.has(m)) continue;
                paid += Number(p.amount || 0);
                const label = METHOD_LABEL[m] || m;
                if (!methods.includes(label)) methods.push(label);
            }
            // Fallback if no payments at all and no method filter: derive
            // "Paid" the same way the invoice ledger surfaces it.
            if (!inv.payments?.length && !filterByMethod) {
                paid = (inv.total || 0) - (inv.balance || 0);
            }
            return {
                "Patient":     inv.patient_name || "",
                "Invoice #":   inv.code || inv.id,
                "Issued":      inv.issued_at ? new Date(inv.issued_at).toISOString().slice(0, 10) : "",
                "Due":         inv.due_date ? new Date(inv.due_date).toISOString().slice(0, 10) : "",
                "Status":      inv.status || "",
                "Amount":      inv.total || 0,
                "Paid":        Math.round(paid * 100) / 100,
                "Balance":     inv.balance || 0,
                "Method(s)":   methods.join(" · ") || "",
            };
        });
        const stamp = new Date().toISOString().slice(0, 10);
        downloadCsv(rows, `invisique-billing-${stamp}.csv`);
        onClose?.();
    };

    const handlePdf = async () => {
        setBusy(true);
        try {
            const { from, to } = resolvedRange;
            const r = await api.post(
                "/doctor/billing/export-pdf",
                {
                    from_date: from,
                    to_date: to,
                    // Only pass the methods list when it's a subset. Passing
                    // `null` keeps the backend on the "include everything"
                    // path so we don't force it to run the filter loop.
                    methods: allMethodsSelected ? null : Array.from(selectedMethods),
                    // No status filter here — the on-screen chip already
                    // shows what's included via the `also applying` line.
                    // Users who want a strict status filter can add it in
                    // a future iteration.
                },
                { responseType: "blob" },
            );
            const stamp = new Date().toISOString().slice(0, 10);
            const blob = new Blob([r.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `invisique-billing-statement-${stamp}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Statement PDF downloaded.");
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not generate PDF.");
        } finally { setBusy(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-3"
            onClick={() => !busy && onClose?.()}
            data-testid="billing-export-dialog"
        >
            <div
                className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-5 pt-5 pb-4 border-b border-border bg-gradient-to-br from-primary/[0.06] to-emerald-100/40">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 grid place-items-center text-primary shrink-0">
                            <DownloadSimple size={20} weight="duotone" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-lg font-bold tracking-tight">Export Billing Report</div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                                Choose a date range and a format. This runs against your currently-filtered view.
                            </div>
                        </div>
                        <button
                            onClick={() => onClose?.()}
                            disabled={busy}
                            className="w-8 h-8 rounded-lg grid place-items-center text-muted-foreground hover:bg-secondary/50 disabled:opacity-40"
                            data-testid="billing-export-close"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="px-5 py-5 space-y-5 overflow-y-auto">
                    {/* Also applying — the read-only "context" strip so the
                        doctor sees what filters the export will inherit
                        from the on-screen state. Explicitly labeled as
                        applied elsewhere so the dialog itself stays lean. */}
                    {(statusChipLabel || searchQuery) && (
                        <div className="rounded-lg border border-border bg-secondary/40 px-3 py-2 flex items-center gap-2 flex-wrap text-[11px]" data-testid="billing-export-context">
                            <Sparkle size={11} weight="fill" className="text-primary" />
                            <span className="uppercase tracking-wider font-bold text-muted-foreground">Also applying:</span>
                            {statusChipLabel && (
                                <span className="px-1.5 py-0.5 rounded-md bg-white border border-border font-semibold">
                                    Status: {statusChipLabel}
                                </span>
                            )}
                            {searchQuery && (
                                <span className="px-1.5 py-0.5 rounded-md bg-white border border-border font-semibold">
                                    Search: “{searchQuery}”
                                </span>
                            )}
                        </div>
                    )}

                    {/* Date range presets */}
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                            <CalendarBlank size={12} /> Date range
                        </div>
                        <div className="flex flex-wrap gap-1.5" role="radiogroup">
                            {PRESETS.map((p) => (
                                <button
                                    key={p.key}
                                    type="button"
                                    role="radio"
                                    aria-checked={preset === p.key}
                                    onClick={() => setPreset(p.key)}
                                    data-testid={`billing-export-preset-${p.key}`}
                                    className={`px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold transition-colors border ${
                                        preset === p.key
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-white text-foreground/80 border-border hover:bg-secondary/60"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        {preset === "custom" && (
                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <label className="text-[11px] font-medium">
                                    <span className="text-muted-foreground uppercase tracking-wider block mb-1">From</span>
                                    <input
                                        type="date"
                                        value={customFrom || ""}
                                        onChange={(e) => setCustomFrom(e.target.value)}
                                        data-testid="billing-export-custom-from"
                                        className="w-full h-9 px-2 rounded-lg border border-border bg-secondary/50 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                                    />
                                </label>
                                <label className="text-[11px] font-medium">
                                    <span className="text-muted-foreground uppercase tracking-wider block mb-1">To</span>
                                    <input
                                        type="date"
                                        value={customTo || ""}
                                        onChange={(e) => setCustomTo(e.target.value)}
                                        data-testid="billing-export-custom-to"
                                        className="w-full h-9 px-2 rounded-lg border border-border bg-secondary/50 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                                    />
                                </label>
                            </div>
                        )}
                        {preset !== "custom" && preset !== "all_time" && (
                            <p className="mt-2 text-[11px] text-muted-foreground">
                                {resolvedRange.from} → {resolvedRange.to}
                            </p>
                        )}
                    </div>

                    {/* Format */}
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Format</div>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setFormat("csv")}
                                data-testid="billing-export-fmt-csv"
                                className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
                                    format === "csv"
                                        ? "border-primary bg-primary/[0.06]"
                                        : "border-border hover:bg-secondary/50"
                                }`}
                            >
                                <Table size={18} className={format === "csv" ? "text-primary" : "text-muted-foreground"} weight="duotone" />
                                <div>
                                    <div className="text-sm font-bold">CSV</div>
                                    <div className="text-[10.5px] text-muted-foreground">Excel · Google Sheets ready</div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormat("pdf")}
                                data-testid="billing-export-fmt-pdf"
                                className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
                                    format === "pdf"
                                        ? "border-primary bg-primary/[0.06]"
                                        : "border-border hover:bg-secondary/50"
                                }`}
                            >
                                <FileText size={18} className={format === "pdf" ? "text-primary" : "text-muted-foreground"} weight="duotone" />
                                <div>
                                    <div className="text-sm font-bold">PDF</div>
                                    <div className="text-[10.5px] text-muted-foreground">Signed statement · CA-friendly</div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Payment methods filter — multi-select chips. Default is
                        "all methods included"; unselecting a chip drops any
                        matching payments from the export and hides invoices
                        that end up with no matching payments. */}
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                            <Wallet size={12} weight="duotone" /> Payment methods
                            <span className="ml-auto normal-case tracking-normal text-[10.5px] font-medium text-muted-foreground/80">
                                {allMethodsSelected
                                    ? "All included"
                                    : noMethodsSelected
                                        ? "None selected"
                                        : `${selectedMethods.size} of ${ALL_METHOD_KEYS.length}`}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by payment method">
                            {METHOD_CHIPS.map((m) => {
                                const checked = selectedMethods.has(m.key);
                                return (
                                    <button
                                        key={m.key}
                                        type="button"
                                        role="checkbox"
                                        aria-checked={checked}
                                        onClick={() => toggleMethod(m.key)}
                                        data-testid={`billing-export-method-${m.key}`}
                                        className={`px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold transition-all border inline-flex items-center gap-1 ${
                                            checked
                                                ? `${m.tone} border ring-1 ring-primary/10`
                                                : "bg-white text-muted-foreground/80 border-border hover:bg-secondary/60"
                                        }`}
                                    >
                                        {checked && (
                                            <Check size={10} weight="bold" className="opacity-80" />
                                        )}
                                        {m.label}
                                    </button>
                                );
                            })}
                        </div>
                        {!allMethodsSelected && !noMethodsSelected && (
                            <p className="mt-2 text-[11px] text-muted-foreground">
                                Only payments in the selected methods will be exported. Invoices with no matching payments are omitted.
                            </p>
                        )}
                        {noMethodsSelected && (
                            <p className="mt-2 text-[11px] text-rose-600 font-medium">
                                Select at least one payment method to enable the download.
                            </p>
                        )}
                    </div>

                    {/* Result preview */}
                    <div className="text-[11.5px] text-muted-foreground">
                        {format === "csv"
                            ? `Ready to export ${rangedCsvRows.length} invoice${rangedCsvRows.length === 1 ? "" : "s"} as CSV${!allMethodsSelected && !noMethodsSelected ? ` · filtered to ${selectedMethods.size} payment method${selectedMethods.size === 1 ? "" : "s"}` : ""}.`
                            : `A statement PDF will be generated on the server and downloaded${!allMethodsSelected && !noMethodsSelected ? `, filtered to ${selectedMethods.size} payment method${selectedMethods.size === 1 ? "" : "s"}` : ""}.`}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 border-t border-border bg-secondary/30 flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => onClose?.()} disabled={busy} className="rounded-full">
                        Cancel
                    </Button>
                    {format === "csv" ? (
                        <Button
                            size="sm"
                            className="rounded-full"
                            onClick={handleCsv}
                            disabled={busy || noMethodsSelected}
                            data-testid="billing-export-submit-csv"
                        >
                            <DownloadSimple size={13} className="mr-1.5" /> Download CSV
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            className="rounded-full"
                            onClick={handlePdf}
                            disabled={busy || noMethodsSelected}
                            data-testid="billing-export-submit-pdf"
                        >
                            <DownloadSimple size={13} className="mr-1.5" />
                            {busy ? "Generating…" : "Download PDF"}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
